import React from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useRootStore } from 'store';
import { TabContextTitle } from '../components/tab-context-title';
import {
  FormRow,
  FormCol4,
  FieldInput,
  FieldCheckBox,
  FieldTextArea,
  toastSuccess,
  toastFailure,
} from 'motion-components';
import { ChemistryValues, FieldSelectChemistry } from '../components/fs-chemistry';
import { FieldSelectBatteryCondition } from '../components/fs-battery-condition';
import { useFormikContext } from 'formik';
import { useFieldDisabledByStatus } from '../services';
import { FaBrain } from 'react-icons/fa';
import { AsyncApiJobResult, AsyncApiJobOutcome, useAsyncApiJob } from '../services/use-asyncapijob';
import AiExtractionInfoPanel from '../components/ai-extraction-info-panel';
import DiffValueDecoration from '../components/diff-value-decoration';
import { ButtonCalcIsAppropriatePackaging } from './components/button-calc-is-appropriate-packaging';
import { ButtonCalcPackagingMaterials } from '../order-packing-tab/components/button-calc-packaging-materials';
import {
  AiPackagingMaterialsCalculationsPanel,
  type AiPackagingMaterialsCalculations,
} from './components/ai-packaging-materials-calculations-panel';

export const OrderOrganizeTab = ({ data, refetch }) => {
  const { appStore } = useRootStore();
  const { t } = useTranslation();
  const { values, setFieldValue, errors } = useFormikContext<any>();
  const fieldDisabled = useFieldDisabledByStatus();

  const AI_FIELD_TO_FORMIK_PATH: Record<string, string[]> = {
    chemistry: ['xrbData.orgChemistry', 'xrbData.chemistry'],
    batteryWeight: ['xrbData.orgBatteryWeight', 'xrbData.batteryWeight'],
    batteryHeight: ['xrbData.orgBatteryHeight', 'xrbData.batteryHeight'],
    batteryLength: ['xrbData.orgBatteryLength', 'xrbData.batteryLength'],
    batteryWidth: ['xrbData.orgBatteryWidth', 'xrbData.batteryWidth'],
    // συμπλήρωσε εδώ επιπλέον mappings όταν χρειαστεί
  };

  const handleTransferFromAi = (key: string, details: any) => {
    let formikPath = AI_FIELD_TO_FORMIK_PATH[key];
    if (!formikPath) {
      formikPath = [`xrbData.${key}`];
    }

    const valueToSet = details.extractedValue;
    if (valueToSet === null || valueToSet === undefined || valueToSet === '') {
      return;
    }
    if (key === 'chemistry') {
      // Θέλει συγκεκριμένες τιμές
      if (!ChemistryValues.includes(valueToSet)) {
        toastFailure(
          `Η Χημική Σύσταση ${valueToSet} δεν είναι έγκυρη. Πρέπει να είναι μία τιμή από τις ακόλουθες: ${ChemistryValues.join(
            ', ',
          )}.`,
        );
        return;
      }
    }
    formikPath.forEach((path) => {
      setFieldValue(path, valueToSet);
    });

    const currentObj = values.xrbData?.aiFileDataExtractionObject ?? {};
    const updatedDetails = {
      ...currentObj[key],
      ...details,
      isAiCorrected: true,
    };
    setFieldValue('xrbData.aiFileDataExtractionObject', {
      ...currentObj,
      [key]: updatedDetails,
    });

    toastSuccess(
      <>
        <span>Η Εξαγόμενη Τιμή (AI) μεταφέρθηκε στην τιμή του χρήστη.</span>
        <br />
        <span> Πρέπει να πατήσετε το κουμπί "Αποθήκευση" για να αποθηκεύσετε τις αλλαγές.</span>
      </>,
    );
  };

  /** Μεταφορά υπολογισμένων υλικών συσκευασίας (AI) στα αντίστοιχα πεδία της φόρμας */
  const handleTransferPackagingFromAi = (
    materialKey: 'vermiculite' | 'aeroplast' | 'plasticBag' | 'polyethylene',
    calculations: AiPackagingMaterialsCalculations,
  ) => {
    const usedKey = `${materialKey}Used`;
    const quantityKey = `${materialKey}Quantity`;
    const aiUsed = calculations[usedKey as keyof AiPackagingMaterialsCalculations];
    const aiQuantity = calculations[quantityKey as keyof AiPackagingMaterialsCalculations];

    setFieldValue(`xrbData.${usedKey}`, aiUsed ?? false);
    setFieldValue(
      `xrbData.${quantityKey}`,
      aiQuantity !== null && aiQuantity !== undefined ? String(aiQuantity) : '',
    );

    const currentObj =
      values.xrbData?.aiPackagingMaterialsCalculationsObject ??
      (() => {
        try {
          return values.xrbData?.aiPackagingMaterialsCalculations
            ? JSON.parse(values.xrbData.aiPackagingMaterialsCalculations)
            : {};
        } catch {
          return {};
        }
      })();

    setFieldValue('xrbData.aiPackagingMaterialsCalculationsObject', {
      ...currentObj,
      ...calculations,
      transferredMaterials: {
        ...(currentObj.transferredMaterials ?? {}),
        [materialKey]: true,
      },
    });

    toastSuccess(
      <>
        <span>Οι τιμές AI για το υλικό μεταφέρθηκαν στη φόρμα.</span>
        <br />
        <span>Πρέπει να πατήσετε το κουμπί "Αποθήκευση" για να αποθηκεύσετε τις αλλαγές.</span>
      </>,
    );
  };

  const triggerAsyncJobUrl = process.env.VITE_TRIGGER_ASYNC_JOB;
  const { runJob, loading: loadingRbCheckOrderRequest } = useAsyncApiJob({
    url: triggerAsyncJobUrl ?? '',
    onJobFinished: async (
      status: AsyncApiJobOutcome,
      job: AsyncApiJobResult | null,
      errorMessage?: string,
    ) => {
      if (status === 'COMPLETED') {
        toastSuccess('Η Ανάλυση Αιτήματος με AI ολοκληρώθηκε επιτυχώς');
        await refetch?.();
      } else if (status === 'FAILED') {
        toastFailure('Η Ανάλυση Αιτήματος με AI απέτυχε');
      } else if (status === 'CANCELLED') {
        toastFailure('Η Ανάλυση Αιτήματος με AI ακυρώθηκε');
      } else if (status === 'EXPIRED') {
        toastFailure('Η Ανάλυση Αιτήματος με AI έληξε');
      } else if (status === 'ERROR') {
        toastFailure(errorMessage ?? 'Αποτυχία εκκίνησης ανάλυσης με AI');
      }
    },
  });
  function handleRbCheckOrderRequest(event: any): void {
    runJob({ jobType: 'rb-check-order', collectionOrderId: data.id });
  }

  return (
    <div>
      <TabContextTitle label={t('Οργάνωση Αιτήματος')} badge="2" />

      {/* Οργάνωση Αιτήματος: Πεδία του Αιτήματος */}
      <div className="form-striped-rows form-striped-rows-start-dark">
        {appStore.isPlatformOwner && (
          <FormRow>
            <FieldInput
              as={FormCol4}
              label={t('Επωνυμία Εισαγωγέα')}
              name="collectionpoint.contributorName"
              disabled
            />
          </FormRow>
        )}

        {/* Ανάλυση Αιτήματος με AI ------------------------------------------------------------ */}
        <FormRow className="mb-3 align-items-center">
          <FormCol4>
            <div className="tab-section-ai-action">
              <Badge pill className="tab-section-badge-outline">
                Α
              </Badge>
              <Button
                name="rbCheckOrderRequest"
                size="sm"
                variant="outline-primary"
                onClick={handleRbCheckOrderRequest}
                disabled={!triggerAsyncJobUrl}
                title={!triggerAsyncJobUrl ? t('Ρύθμιση VITE_TRIGGER_ASYNC_JOB απαιτείται') : undefined}
              >
                {loadingRbCheckOrderRequest ? (
                  <span className="mb-1 me-2">
                    <Spinner animation="border" size="sm" />
                  </span>
                ) : (
                  <FaBrain className="mb-1 me-2" />
                )}
                Ανάλυση Αιτήματος με AI
              </Button>
            </div>
          </FormCol4>

          {values.xrbData?.aiFileDataExtractionObject && (
            <AiExtractionInfoPanel
              aiFileDataExtractionObject={values.xrbData.aiFileDataExtractionObject}
              onTransferValue={handleTransferFromAi}
            />
          )}
        </FormRow>
        <FormRow>
          <DiffValueDecoration as={FormCol4} xrbData={values.xrbData} orgField="orgChemistry">
            <FieldSelectChemistry
              name="xrbData.orgChemistry"
              label={t('Χημική σύσταση μπαταρίας (Οργάνωση)')}
              disabled={fieldDisabled['xrbData.orgChemistry']}
            />
          </DiffValueDecoration>

          <DiffValueDecoration as={FormCol4} xrbData={values.xrbData} orgField="orgBatteryWeight">
            <FieldInput
              label={t('Βάρος μπαταρίας (kg/τμχ) (Οργάνωση)')}
              name="xrbData.orgBatteryWeight"
              placeholder=""
              disabled={fieldDisabled['xrbData.orgBatteryWeight']}
            />
          </DiffValueDecoration>

          <DiffValueDecoration as={FormCol4} xrbData={values.xrbData} orgField="orgPieceCount">
            <FieldInput
              label={t('Πλήθος τεμαχίων (Οργάνωση)')}
              name="xrbData.orgPieceCount"
              placeholder=""
              disabled={fieldDisabled['xrbData.orgPieceCount']}
            />
          </DiffValueDecoration>
        </FormRow>

        <FormRow>
          <DiffValueDecoration as={FormCol4} xrbData={values.xrbData} orgField="orgBatteryHeight">
            <FieldInput
              label={t('Ύψος μπαταρίας (cm) (Οργάνωση)')}
              name="xrbData.orgBatteryHeight"
              placeholder=""
              disabled={fieldDisabled['xrbData.orgBatteryHeight']}
            />
          </DiffValueDecoration>

          <DiffValueDecoration as={FormCol4} xrbData={values.xrbData} orgField="orgBatteryLength">
            <FieldInput
              label={t('Μήκος μπαταρίας (cm) (Οργάνωση)')}
              name="xrbData.orgBatteryLength"
              placeholder=""
              disabled={fieldDisabled['xrbData.orgBatteryLength']}
            />
          </DiffValueDecoration>

          <DiffValueDecoration as={FormCol4} xrbData={values.xrbData} orgField="orgBatteryWidth">
            <FieldInput
              label={t('Πλάτος μπαταρίας (cm) (Οργάνωση)')}
              name="xrbData.orgBatteryWidth"
              placeholder=""
              disabled={fieldDisabled['xrbData.orgBatteryWidth']}
            />
          </DiffValueDecoration>
        </FormRow>

        <FormRow>
          <DiffValueDecoration as={FormCol4} xrbData={values.xrbData} orgField="batteryCondition">
            <FieldSelectBatteryCondition
              name="xrbData.batteryCondition"
              label={t('Κατάσταση μπαταρίας')}
              disabled={fieldDisabled['xrbData.batteryCondition']}
            />
          </DiffValueDecoration>
        </FormRow>

        {/* 2 Υπολογισμός Καταλληλότητας συσκευασίας με AI — 3 στήλες όπως οι υπόλοιπες σειρές */}
        <FormRow className="form-row-ai-packaging-inline">
          <FormCol4>
            <div className="tab-section-ai-action mb-2">
              <Badge pill className="tab-section-badge-outline">
                Β
              </Badge>
              <ButtonCalcIsAppropriatePackaging refetch={refetch} />
            </div>
          </FormCol4>

          <FormCol4>
            {values.xrbData?.aiIsAppropriatePackaging != null && (
              <FieldCheckBox
                name="xrbData.aiIsAppropriatePackaging"
                label={t('Κατάλληλη συσκευασία (AI)')}
                disabled
              />
            )}
          </FormCol4>

          <FormCol4>
            {values.xrbData?.aiIsAppropriatePackagingReasoning && (
              <FieldTextArea
                name="xrbData.aiIsAppropriatePackagingReasoning"
                label={t('Αιτιολόγηση AI καταλληλότητας συσκευασίας')}
                disabled
                rows={1}
              />
            )}
          </FormCol4>
        </FormRow>

        <FormRow>
          <FieldCheckBox
            as={FormCol4}
            name="xrbData.appropriatePackaging"
            label={t('Κατάλληλη συσκευασία')}
            disabled={fieldDisabled['xrbData.appropriatePackaging']}
          />
          {!values.xrbData?.appropriatePackaging && (
            <FieldInput
              as={FormCol4}
              name="xrbData.requiredPackaging"
              label={t('Περιγραφή Συσκευασίας για Αποστολή')}
              disabled={fieldDisabled['xrbData.requiredPackaging']}
            />
          )}
        </FormRow>

        {/* 3 Υπολογισμός Υλικών Συσκευασίας ------------------------------------------------------------------- */}
        <FormRow className="mb-3 mt-3">
          <FormCol4>
            <div className="tab-section-ai-action">
              <Badge pill className="tab-section-badge-outline">
                Γ
              </Badge>
              <ButtonCalcPackagingMaterials refetch={refetch} />
            </div>
          </FormCol4>
          {(values.xrbData?.aiPackagingMaterialsCalculationsObject != null ||
            values.xrbData?.aiPackagingMaterialsCalculations) && (
            <AiPackagingMaterialsCalculationsPanel
              aiPackagingMaterialsCalculations={
                values.xrbData.aiPackagingMaterialsCalculationsObject ??
                values.xrbData.aiPackagingMaterialsCalculations
              }
              currentValues={values.xrbData}
              onTransferMaterial={handleTransferPackagingFromAi}
            />
          )}
        </FormRow>
        {/* end of Υπολογισμός Υλικών Συσκευασίας ------------------------------------------------------------ */}

        <FormRow>
          <FieldCheckBox
            as={FormCol4}
            name="xrbData.vermiculiteUsed"
            label={t('Χρήση βερμικουλίτη')}
            disabled={fieldDisabled['xrbData.vermiculiteUsed']}
          />
          {values.xrbData?.vermiculiteUsed && (
            <FieldInput
              as={FormCol4}
              name="xrbData.vermiculiteQuantity"
              label={t('Ποσότητα βερμικουλίτη')}
              disabled={fieldDisabled['xrbData.vermiculiteQuantity']}
            />
          )}
        </FormRow>
        <FormRow>
          <FieldCheckBox
            as={FormCol4}
            name="xrbData.aeroplastUsed"
            label={t('Χρήση αεροπλαστ')}
            disabled={fieldDisabled['xrbData.aeroplastUsed']}
          />
          {values.xrbData?.aeroplastUsed && (
            <FieldInput
              as={FormCol4}
              name="xrbData.aeroplastQuantity"
              label={t('Ποσότητα αεροπλαστ')}
              disabled={fieldDisabled['xrbData.aeroplastQuantity']}
            />
          )}
        </FormRow>
        <FormRow>
          <FieldCheckBox
            as={FormCol4}
            name="xrbData.plasticBagUsed"
            label={t('Χρήση πλαστικής σακούλας')}
            disabled={fieldDisabled['xrbData.plasticBagUsed']}
          />
          {values.xrbData?.plasticBagUsed && (
            <FieldInput
              as={FormCol4}
              name="xrbData.plasticBagQuantity"
              label={t('Ποσότητα πλαστικής σακούλας')}
              disabled={fieldDisabled['xrbData.plasticBagQuantity']}
            />
          )}
        </FormRow>
        <FormRow>
          <FieldCheckBox
            as={FormCol4}
            name="xrbData.polyethyleneUsed"
            label={t('Χρήση πολυαιθυλενίου')}
            disabled={fieldDisabled['xrbData.polyethyleneUsed']}
          />
          {values.xrbData?.polyethyleneUsed && (
            <FieldInput
              as={FormCol4}
              name="xrbData.polyethyleneQuantity"
              label={t('Ποσότητα πολυαιθυλενίου')}
              disabled={fieldDisabled['xrbData.polyethyleneQuantity']}
            />
          )}
        </FormRow>
        <FormRow>
          <FieldInput
            as={FormCol4}
            name="xrbData.otherMaterial"
            label={t('Χρήση άλλου υλικού')}
            disabled={fieldDisabled['xrbData.otherMaterial']}
          />
        </FormRow>
        <FormRow>
          <FieldInput
            as={FormCol4}
            name="xrbData.totalRequestWeight"
            label={t('Συνολικό βάρος αιτήματος')}
            disabled={fieldDisabled['xrbData.totalRequestWeight']}
          />
        </FormRow>
      </div>
    </div>
  );
};
